import { ResizeNotifier, prefersReducedMotion, yieldToMainThread, debounce } from '@theme/utilities';
import { Component } from '@theme/component';
import { getScrollTop, getScrollEventTarget, getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Scroll-driven timeline line growth and item reveal animations.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} line
 * @property {HTMLElement} list
 *
 * @extends {Component<Refs>}
 */
export class SkreyTimeline extends Component {
  requiredRefs = ['line', 'list'];

  /** @type {ResizeNotifier | null} */
  #resizeObserver = null;

  /** @type {ReturnType<typeof debounce> | null} */
  #resizeListener = null;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {(() => void) | null} */
  #mobileQueryListener = null;

  /** @type {(() => void) | null} */
  #squeezeQueryListener = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {(() => void) | null} */
  #scrollListener = null;

  /** @type {boolean} */
  #scrollTicking = false;

  connectedCallback() {
    super.connectedCallback();

    const shouldAnimate = this.dataset.animate === 'true' && !prefersReducedMotion();

    this.#bindResizeTracking();
    this.#bindScrollListener();
    this.#scheduleLineGeometryUpdate();

    if (!shouldAnimate) {
      this.#showAll();
      return;
    }

    yieldToMainThread().then(() => this.#updateScrollState());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#unbindScrollListener();

    if (this.#resizeListener) {
      window.removeEventListener('resize', this.#resizeListener);
    }

    if (this.#mobileQuery && this.#mobileQueryListener) {
      this.#mobileQuery.removeEventListener('change', this.#mobileQueryListener);
    }

    if (this.#squeezeQueryListener) {
      scrollContainerMediaQuery.removeEventListener('change', this.#squeezeQueryListener);
    }
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#scheduleLineGeometryUpdate();
    this.#updateScrollState();
  }

  #bindScrollListener() {
    this.#unbindScrollListener();

    this.#scrollTarget = getScrollEventTarget();
    this.#scrollListener = () => {
      if (this.#scrollTicking) return;

      this.#scrollTicking = true;
      requestAnimationFrame(() => {
        this.#updateScrollState();
        this.#scrollTicking = false;
      });
    };

    this.#scrollTarget.addEventListener('scroll', this.#scrollListener, { passive: true });

    if (!this.#squeezeQueryListener) {
      this.#squeezeQueryListener = () => {
        this.#bindScrollListener();
        this.#updateScrollState();
      };
      scrollContainerMediaQuery.addEventListener('change', this.#squeezeQueryListener);
    }
  }

  #unbindScrollListener() {
    if (this.#scrollTarget && this.#scrollListener) {
      this.#scrollTarget.removeEventListener('scroll', this.#scrollListener);
    }
  }

  #bindResizeTracking() {
    this.#resizeListener = debounce(() => this.#scheduleLineGeometryUpdate(), 150);
    window.addEventListener('resize', this.#resizeListener);

    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#mobileQueryListener = () => this.#scheduleLineGeometryUpdate();
    this.#mobileQuery.addEventListener('change', this.#mobileQueryListener);

    this.#resizeObserver = new ResizeNotifier(() => this.#scheduleLineGeometryUpdate());
    this.#resizeObserver.observe(this.refs.list);

    document.fonts?.ready?.then(() => this.#scheduleLineGeometryUpdate());
  }

  #scheduleLineGeometryUpdate() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateLineGeometry();

        if (this.dataset.animate === 'true') {
          this.#updateScrollState();
        }
      });
    });
  }

  #showAll() {
    this.style.setProperty('--skrey-line-progress', '1');
    this.querySelectorAll('[data-timeline-item]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.style.setProperty('--item-reveal', '1');
      item.style.setProperty('--dot-fill', '1');
    });
  }

  /**
   * @param {Element} dot
   * @returns {number}
   */
  #dotCenterY(dot) {
    const rect = dot.getBoundingClientRect();
    return getScrollTop() + rect.top + rect.height / 2;
  }

  #updateLineGeometry() {
    const items = this.querySelectorAll('[data-timeline-item]');
    const rail = this.querySelector('.skrey-timeline__rail');

    if (!rail || items.length === 0) {
      this.style.setProperty('--skrey-line-height', '0px');
      return;
    }

    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    const firstDot = firstItem?.querySelector('.skrey-timeline__dot');
    const lastDot = lastItem?.querySelector('.skrey-timeline__dot');

    if (!firstDot || !lastDot) {
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const firstRect = firstDot.getBoundingClientRect();
    const lastRect = lastDot.getBoundingClientRect();

    const top = firstRect.top + firstRect.height / 2 - railRect.top;
    const bottom = lastRect.top + lastRect.height / 2 - railRect.top;
    const height = Math.max(0, bottom - top);

    this.style.setProperty('--skrey-line-top', `${top}px`);
    this.style.setProperty('--skrey-line-height', `${height}px`);
  }

  #updateScrollState() {
    if (this.dataset.animate !== 'true') return;

    const items = this.querySelectorAll('[data-timeline-item]');

    if (items.length === 0) return;

    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    const firstDot = firstItem?.querySelector('.skrey-timeline__dot');
    const lastDot = lastItem?.querySelector('.skrey-timeline__dot');

    if (!firstDot || !lastDot) return;

    const scrollContainer = getScrollContainer();
    const scrollTop = getScrollTop();
    const viewportHeight =
      scrollContainer instanceof Element ? scrollContainer.clientHeight : window.innerHeight;
    const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    const isAtScrollEnd = scrollTop >= maxScrollTop - 4;

    const triggerY = scrollTop + viewportHeight * 0.9;
    const firstCenterY = this.#dotCenterY(firstDot);
    const lastCenterY = this.#dotCenterY(lastDot);
    const span = lastCenterY - firstCenterY;

    if (span <= 0) {
      this.#showAll();
      return;
    }

    let lineProgress = Math.min(1, Math.max(0, (triggerY - firstCenterY) / span));

    if (isAtScrollEnd) {
      lineProgress = 1;
    }
    this.style.setProperty('--skrey-line-progress', String(lineProgress));

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const dot = item.querySelector('.skrey-timeline__dot');
      if (!dot) return;

      const dotCenterY = this.#dotCenterY(dot);
      const itemPoint = (dotCenterY - firstCenterY) / span;
      const revealStart = Math.max(0, itemPoint - 0.1);
      const revealEnd = Math.min(1, itemPoint + 0.02);
      const revealRange = Math.max(0.001, revealEnd - revealStart);
      const itemReveal = Math.min(1, Math.max(0, (lineProgress - revealStart) / revealRange));

      const dotLead = 0.04;
      const dotFillStart = Math.max(0, itemPoint - dotLead);
      const dotFillEnd = Math.min(1, Math.max(dotFillStart + 0.001, itemPoint + 0.03));
      const dotFillRange = dotFillEnd - dotFillStart;
      let dotFill = Math.min(1, Math.max(0, (lineProgress - dotFillStart) / dotFillRange));

      if (isAtScrollEnd || lineProgress >= dotFillEnd) {
        dotFill = 1;
      }

      item.style.setProperty('--item-reveal', String(itemReveal));
      item.style.setProperty('--dot-fill', String(dotFill));
    });
  }
}

if (!customElements.get('skrey-timeline')) {
  customElements.define('skrey-timeline', SkreyTimeline);
}
